import React from 'react'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { Popconfirm, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { CountBallAbsoluteRight, CountBallInline, Stage } from '../../components/activity-timeline/styles'
import { ButtonActionWrapper } from '../../components/list-operations/styles'
import { Value } from '../../pages_v2/elements'
import { Sorter } from '../../interfaces'

const formatPhone = (num: string) => {
  if (!num || num.length !== 11) return null
  return num.replace(/^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/, '+$1 ($2) $3-$4-$5')
}

export const getColumns = (sorter: Sorter, enums: any, stages: any[], canDelete: boolean, onDelete: (id: number) => void) => {
  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      width: 80,
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      fixed: 'left',
      sortOrder: sorter?.sort_field === 'id' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Стадия',
      width: 160,
      dataIndex: 'stage',
      key: 'stage',
      sorter: true,
      fixed: 'left',
      sortOrder: sorter?.sort_field === 'stage' ? sorter?.sort_order : undefined,
      render: (stage: any, data) => {
        const current = typeof stage === 'object' ? stage : stages?.find(item => item.code === stage)
        const style = current?.['style']
        const { avito_chat_count } = data
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {current ? (
              <Stage style={style}>{current['name']}</Stage>
            ) : (
              <span>{stage}</span>
            )}
            {avito_chat_count ? (
              <CountBallAbsoluteRight title={'avito_chat_count'} bgColor={'countColor'}>
                {avito_chat_count}
              </CountBallAbsoluteRight>
            ) : null}
          </div>
        )
      },
    },
    {
      title: 'Чаты',
      width: 60,
      dataIndex: 'chat_count',
      key: 'chat_count',
      sorter: true,
      sortOrder: sorter?.sort_field === 'chat_count' ? sorter?.sort_order : undefined,
      render: (_, data) => {
        const { chat_count } = data
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {chat_count ? (
              <CountBallInline title={'chat_count'} bgColor={'countColor'}>
                {chat_count > 99 ? 99 : chat_count}
              </CountBallInline>
            ) : (
              '-'
            )}
          </div>
        )
      },
    },
    {
      title: 'Отдел',
      width: 150,
      dataIndex: 'department',
      key: 'department',
      sorter: true,
      sortOrder: sorter?.sort_field === 'department' ? sorter?.sort_order : undefined,
      render: (v: number) =>
        enums?.department ? enums.department.find(i => i.value === v)?.label : null,
    },
    {
      title: 'Вакансия',
      width: 150,
      dataIndex: 'vacancy',
      key: 'vacancy',
      sorter: true,
      sortOrder: sorter?.sort_field === 'vacancy' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Менеджер',
      width: 150,
      dataIndex: 'manager_id',
      key: 'manager_id',
      sorter: true,
      sortOrder: sorter?.sort_field === 'manager_id' ? sorter?.sort_order : undefined,
      render: (v, row) => {
        const manager = v || row.created_by
        return enums?.interview_responsible
          ? enums.interview_responsible.find(i => i.value === manager)?.label
          : null
      },
    },
    {
      title: 'Ответственный за собеседование',
      width: 150,
      dataIndex: 'interview_responsible',
      key: 'interview_responsible',
      render: (v: number) =>
        enums?.interview_responsible
          ? enums.interview_responsible.find(i => i.value === v)?.label
          : null,
    },
    {
      title: 'Дата создания',
      width: 150,
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: sorter?.sort_field === 'created_at' ? sorter?.sort_order : undefined,
      render: (val: string | null) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Дата первого контакта',
      dataIndex: 'first_contact_date',
      key: 'first_contact_date',
      width: 130,
      render: val => <Value data={val} />,
    },
    {
      title: 'Дата завершения',
      width: 150,
      dataIndex: 'completed_at',
      key: 'completed_at',
      sorter: true,
      sortOrder: sorter?.sort_field === 'completed_at' ? sorter?.sort_order : undefined,
      render: (val: string | null) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'ФИО кандидата',
      width: 150,
      dataIndex: 'fio',
      key: 'fio',
      sorter: true,
      sortOrder: sorter?.sort_field === 'fio' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Тип собеседования',
      width: 150,
      dataIndex: 'interview_type',
      key: 'interview_type',
      sorter: true,
      sortOrder: sorter?.sort_field === 'interview_type' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Телефон',
      width: 150,
      dataIndex: 'phone',
      key: 'phone',
      sorter: true,
      sortOrder: sorter?.sort_field === 'phone' ? sorter?.sort_order : undefined,
      render: v => <>{formatPhone(String(v))}</>,
    },
    {
      title: 'Возраст',
      width: 50,
      dataIndex: 'age',
      key: 'age',
      sorter: true,
      sortOrder: sorter?.sort_field === 'age' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Дата собеседования',
      width: 150,
      dataIndex: 'datetime_interview',
      key: 'datetime_interview',
      sorter: true,
      sortOrder: sorter?.sort_field === 'datetime_interview' ? sorter?.sort_order : undefined,
      render: (val: string | null) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Дата задачи',
      width: 150,
      dataIndex: 'task_deadline',
      key: 'task_deadline',
      sorter: true,
      sortOrder: sorter?.sort_field === 'task_deadline' ? sorter?.sort_order : undefined,
      render: (val: null | string) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Ресурс',
      width: 150,
      dataIndex: 'resource',
      key: 'resource',
      sorter: true,
      sortOrder: sorter?.sort_field === 'resource' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Округ',
      width: 150,
      dataIndex: 'federal_district_id',
      key: 'federal_district_id',
      sorter: true,
      sortOrder: sorter?.sort_field === 'federal_district' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Регион',
      width: 150,
      dataIndex: 'region_id',
      key: 'region_id',
      sorter: true,
      sortOrder: sorter?.sort_field === 'region' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Город',
      width: 150,
      dataIndex: 'city_id',
      key: 'city_id',
      sorter: true,
      sortOrder: sorter?.sort_field === 'city' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Район',
      width: 150,
      dataIndex: 'district_id',
      key: 'district_id',
      sorter: true,
      sortOrder: sorter?.sort_field === 'district' ? sorter?.sort_order : undefined,
    },
    {
      title: 'Дата выхода на работу',
      width: 200,
      dataIndex: 'start_probation_period',
      key: 'start_probation_period',
      sorter: true,
      sortOrder: sorter?.sort_field === 'start_probation_period' ? sorter?.sort_order : undefined,
      render: (val: string | null) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Дата окончания исп.срока',
      width: 200,
      dataIndex: 'end_probation_period',
      key: 'end_probation_period',
      sorter: true,
      sortOrder: sorter?.sort_field === 'end_probation_period' ? sorter?.sort_order : undefined,
      render: (val: null | string) => val && dayjs(val).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Причина отказа',
      width: 150,
      dataIndex: 'cancel_reason',
      key: 'cancel_reason',
      sorter: true,
      sortOrder: sorter?.sort_field === 'cancel_reason' ? sorter?.sort_order : undefined,
      render: (v: number) =>
        enums?.cancel_reason ? enums.cancel_reason.find(i => i.value === v)?.label : null,
    },
  ]

  if (canDelete) {
    columns.push({
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      width: 50,
      align: 'center',
      fixed: 'right',
      render: (_: any, record) => (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <Typography.Link>
            <ButtonActionWrapper title='Удалить'>
              <Popconfirm title='Удалить поле?' onConfirm={() => onDelete(record.id)}>
                <DeleteOutlined />
              </Popconfirm>
            </ButtonActionWrapper>
          </Typography.Link>
        </span>
      ),
    })
  }

  return columns
}

